<template>
  <div class="content-preferences-page">
    <h4 class="font-weight-bolder mb-1">
      Content Preferences
    </h4>
    <p class="text-muted mb-2">
      Manage categories this user prefers to see in the mobile feed.
    </p>

    <div v-if="isLoading" class="text-center py-2">
      <b-spinner label="Loading preferences..." />
    </div>
    <b-alert v-else-if="loadError" show variant="danger">
      {{ loadError }}
      <b-button size="sm" variant="outline-danger" class="ml-1" @click="fetchPreferences">Retry</b-button>
    </b-alert>

    <b-card v-else no-body>
      <b-card-body>
        <b-row>
          <b-col
            v-for="cat in categories"
            :key="cat.id"
            md="4"
            sm="6"
            class="mb-1"
          >
            <b-form-checkbox v-model="cat.selected">
              {{ cat.name }}
            </b-form-checkbox>
          </b-col>
        </b-row>
        <div class="mt-2">
          <b-button
            variant="primary"
            :disabled="saving"
            @click="save"
          >
            Save Preferences
          </b-button>
          <b-button
            variant="outline-secondary"
            class="ml-1"
            :disabled="saving"
            @click="reset"
          >
            Reset to Default
          </b-button>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BAlert,
  BButton,
  BCard,
  BCardBody,
  BCol,
  BFormCheckbox,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import { adminPanelApi } from '@/services/adminPanelApi'
import { unwrapAdminResponse } from '@/utils/adminApiHelpers'
import { notifyError, notifySuccess } from '@/utils/uiActions'

export default {
  name: 'ContentPreferences',
  components: {
    BAlert,
    BButton,
    BCard,
    BCardBody,
    BCol,
    BFormCheckbox,
    BRow,
    BSpinner,
  },
  data() {
    return {
      isLoading: false,
      loadError: '',
      saving: false,
      categories: [],
    }
  },
  computed: {
    userId() {
      return this.$route.params.id
    },
  },
  created() {
    this.fetchPreferences()
  },
  methods: {
    async fetchPreferences() {
      if (!this.userId) return
      this.isLoading = true
      this.loadError = ''
      try {
        const res = await adminPanelApi.userContentPreferences(this.userId)
        const payload = unwrapAdminResponse(res)
        const list = payload.categories || []
        this.categories = list.map(c => ({
          id: c.id,
          name: c.name,
          selected: Boolean(c.is_selected),
        }))
      } catch (e) {
        this.loadError = e.response?.data?.message || 'Unable to load preferences.'
        this.categories = []
      } finally {
        this.isLoading = false
      }
    },
    async save() {
      this.saving = true
      try {
        const categoryIds = this.categories.filter(c => c.selected).map(c => c.id)
        await adminPanelApi.updateUserContentPreferences(this.userId, categoryIds)
        notifySuccess(this, 'Preferences saved successfully.')
      } catch (e) {
        notifyError(this, e.response?.data?.message || 'Unable to save preferences.')
      } finally {
        this.saving = false
      }
    },
    async reset() {
      this.saving = true
      try {
        await adminPanelApi.resetUserContentPreferences(this.userId)
        await this.fetchPreferences()
        notifySuccess(this, 'Preferences reset successfully.')
      } catch (e) {
        notifyError(this, e.response?.data?.message || 'Unable to reset preferences.')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
